'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { usePathname } from 'next/navigation';
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';

import type { CatalogItem } from './header-data';

import Link from '@eliadmin/core/src/components/link';
import { CmsIcon } from '@eliadmin/core/src/components/dlz/ui/cms-image';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { PromoTip } from './promo-tip';

/** Where the drawer starts: under the header, aligned with the header content edges. */
type Geometry = { top: number; left: number; right: number };

/** Sub-categories listed in the panel; the rest is behind „Zobraziť viac (+N)“ (the whole category). */
const PANEL_LIMIT = 12;
/** Pointer rest before a hovered entry opens – crossing other rows on the way to the panel opens none. */
const HOVER_DELAY = 120;

const ACCENT_LINK =
  'dlz-body-m-medium flex w-fit items-center gap-1 text-[#D7191F] hover:text-[#B5141A]';

const ROW =
  'flex h-11 w-full items-center gap-3 rounded-[18px] px-4 text-left text-[#0F1012] transition-colors hover:bg-[#ECEDEF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F1012]';

/**
 * „Kategórie“ button + catalogue drawer (Figma „Open katalog drawer“ 60029:4182): the catalogue
 * entries from the administration on the left (60029:5594); hovering or clicking an entry shows its
 * direct sub-categories one below the other in the white panel on the right (at most 12, then
 * „Zobraziť viac (+N)“) next to the entry's tip (60029:5802) – or the first tip of the catalogue.
 * The design unfolds the entry as an accordion – on request of the client the panel lists its
 * content instead. The page is dimmed below the header (60029:5108). Desktop only – phones have
 * the categories panel of the mobile navbar.
 */
export function CatalogDrawer({ catalog }: { catalog: CatalogItem[] }) {
  const { t } = useTranslations();
  const pathname = usePathname();
  const trigger = useRef<HTMLButtonElement>(null);
  const allLink = useRef<HTMLAnchorElement>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  const [geometry, setGeometry] = useState<Geometry | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [focusPanel, setFocusPanel] = useState(false);
  const panelId = useId();

  useEffect(() => setGeometry(null), [pathname]);

  useEffect(() => {
    if (!focusPanel) return;
    allLink.current?.focus();
    setFocusPanel(false);
  }, [focusPanel]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  const onOpenChange = (open: boolean) => {
    const button = trigger.current;
    const row = button?.closest('[data-header-row]')?.getBoundingClientRect();
    const header = button?.closest('header')?.getBoundingClientRect();
    window.clearTimeout(hoverTimer.current);
    setActiveId(null);
    setGeometry(
      open && row && header
        ? { top: header.bottom, left: row.left, right: window.innerWidth - row.right }
        : null,
    );
  };

  const active = catalog.find(item => item.id === activeId);
  // Entries without their own tip show the first one filled in the administration.
  const tip = active?.tip ?? catalog.find(item => item.tip)?.tip ?? null;
  const below: CSSProperties | undefined = geometry ? { top: geometry.top } : undefined;

  const open = (item: CatalogItem, focus = false) => {
    window.clearTimeout(hoverTimer.current);
    setActiveId(item.id);
    if (focus) setFocusPanel(true);
  };

  return (
    <Dialog.Root open={geometry !== null} onOpenChange={onOpenChange}>
      <Dialog.Trigger
        ref={trigger}
        className="dlz-button-m flex h-[46px] shrink-0 items-center gap-2 rounded-full bg-[#D7191F] pl-4 pr-5 text-white transition-colors hover:bg-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] focus-visible:ring-offset-2"
      >
        <Icon name="menu" size={24} />
        <span className="dlz-trim">{t('text176')}</span>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay style={below} className="fixed inset-x-0 bottom-0 z-40 bg-[#0F1012]/30" />
        <Dialog.Content
          aria-describedby={undefined}
          style={below}
          onClick={event => {
            // The content spans the dimmed area; a click there (not on the panels) closes.
            if (event.target === event.currentTarget) onOpenChange(false);
            // A link to the current page does not change the pathname.
            else if ((event.target as HTMLElement).closest('a')) onOpenChange(false);
          }}
          className="fixed inset-x-0 bottom-0 z-40 flex items-start focus:outline-none"
        >
          <Dialog.Title className="sr-only">{t('text176')}</Dialog.Title>
          <div
            style={{ paddingLeft: geometry?.left }}
            className="flex h-full shrink-0 items-start gap-6 overflow-y-auto overscroll-contain bg-[#F5F6F7] py-4 pr-3"
          >
            <Dialog.Close
              aria-label={t('text272')}
              className="mt-2 rounded-md text-[#0F1012] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
            >
              <Icon name="x" size={24} />
            </Dialog.Close>
            <ul className="w-[367px]">
              {catalog.map(item => {
                const current = item.id === activeId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      aria-expanded={current}
                      aria-controls={current ? panelId : undefined}
                      // Keyboard activation (no pointer → detail 0) continues in the panel.
                      onClick={event => open(item, event.detail === 0)}
                      onKeyDown={event => {
                        if (event.key !== 'ArrowRight') return;
                        event.preventDefault();
                        open(item, true);
                      }}
                      onPointerEnter={event => {
                        if (event.pointerType !== 'mouse') return;
                        window.clearTimeout(hoverTimer.current);
                        hoverTimer.current = window.setTimeout(() => open(item), HOVER_DELAY);
                      }}
                      onPointerLeave={() => window.clearTimeout(hoverTimer.current)}
                      className={cn(ROW, current && 'bg-[#ECEDEF] text-[#D7191F]')}
                    >
                      <CmsIcon image={item.icon} className="size-6" />
                      <span className="dlz-body-s-medium flex-1">{item.name}</span>
                      <Icon name="caret-right" size={18} className="text-[#D7191F]" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          {active ? (
            <div
              id={panelId}
              role="region"
              aria-label={active.name}
              style={{ paddingRight: geometry?.right }}
              className="flex max-h-full flex-1 items-start gap-6 overflow-y-auto overscroll-contain bg-white p-6"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-6">
                <p className="dlz-h5 text-[#0F1012]">{active.name}</p>
                <ul className="flex flex-col gap-4">
                  <li>
                    <Link ref={allLink} href={active.href} prefetch={false} className={ACCENT_LINK}>
                      {t('text3475')}
                      <Icon name="caret-right" size={20} />
                    </Link>
                  </li>
                  {active.children.slice(0, PANEL_LIMIT).map(node => (
                    <li key={node.id}>
                      <Link
                        href={node.href}
                        prefetch={false}
                        className="dlz-body-m text-[#4F545C] transition-colors hover:text-[#0F1012]"
                      >
                        {node.name}
                      </Link>
                    </li>
                  ))}
                  {active.children.length > PANEL_LIMIT ? (
                    <li>
                      <Link href={active.href} prefetch={false} className={ACCENT_LINK}>
                        {`${t('text338')} (+${active.children.length - PANEL_LIMIT})`}
                        <Icon name="caret-right" size={20} />
                      </Link>
                    </li>
                  ) : null}
                </ul>
              </div>
              {tip ? <PromoTip tip={tip} className="h-80 w-[min(450px,55%)] shrink-0" /> : null}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
